import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import {
    AddCircleOutline,
    ArrowBack,
    AutorenewOutlined,
    CalendarToday,
    CreditCard,
    DeleteOutline,
    EmailOutlined,
    EventRepeat,
    PaymentsOutlined,
    PersonAdd,
    Pix,
    Receipt,
    ViewAgendaOutlined,
    WhatsApp,
} from '@mui/icons-material'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { api } from '../../../services/Main/Api'
import { getCondoId } from '../../../services/Main/AuthStorage'
import Context from '../../../contexts/Context'
import Masks from '../../../components/Shared/Masks'

const CONTACT_TYPE_MAP = {
    CLIENT: { label: 'Cliente', color: '#3B82F6' },
    EMPLOYEE: { label: 'Funcionário', color: '#8B5CF6' },
    SUPPLIER: { label: 'Fornecedor', color: '#F59E0B' },
    BUSINESS_PARTNER: { label: 'Sócio', color: '#22C55E' },
}

const ReceivablesCreate = () => {
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const { setLoadingModal, getUnitOptions, unitOptions } = useContext(Context)

    const [reqData, setReqData] = useState({
        due_date: moment().add(1, 'day').format(),
        send_email: true,
        send_whatsapp: true,
        charge_type: 'one_time', // 'one_time', 'installment', 'recurring'
        installments: '',
        recurrence: '', // 'daily', 'weekly', 'monthly', 'quarterly', 'semiannual', 'annual'
        recurrence_start: new Date(),
        recurrence_end: null, // null = vitalício
        no_end_date: true,
        payment_methods: ['boleto', 'pix'],
        reference_date: new Date(),
        generate_days_before: 5, // gerar fatura X dias antes do vencimento
    })

    const [items, setItems] = useState([{ description: '', total: '', category_fee: null }])
    const [categories, setCategories] = useState([])
    const [categoryFees, setCategoryFees] = useState([])
    const [accounts, setAccounts] = useState([])
    const [saving, setSaving] = useState(false)
    const [selectedUnit, setSelectedUnit] = useState(null)
    const [selectedAccount, setSelectedAccount] = useState(null)
    const isFitBankAccount = selectedAccount?.is_fitbank === true
    const [contactData, setContactData] = useState(null) // { name, cpf, email, phone }

    const computedTotal = items.reduce((sum, item) => {
        const raw = item.total || ''
        const isNegative = raw.trim().startsWith('-')
        const val = +(raw.replace(/[^\d]/g, '') || 0)
        return sum + (isNegative ? -val : val)
    }, 0)

    // Fetch contact details when unit changes
    useEffect(() => {
        if (!reqData.unit) {
            setContactData(null)
            return
        }
        // Only fetch if we don't already have data for this contact (e.g. from inline creation)
        if (contactData && contactData._unitId === reqData.unit) return

        api.get(`/unit/${reqData.unit}/`).then((res) => {
            const u = res.data
            const hasEmail = !!u.email
            const hasPhone = !!u.phone
            setContactData({
                _unitId: reqData.unit,
                name: u.name || '',
                cpf: u.cpf || '',
                email: u.email || '',
                phone: u.phone || '',
            })
            // Auto-disable notifications if contact lacks email/phone
            setReqData((prev) => ({
                ...prev,
                send_email: hasEmail ? prev.send_email : false,
                send_whatsapp: hasPhone ? prev.send_whatsapp : false,
            }))
        }).catch(() => {})
    }, [reqData.unit])

    // Fetch unit options on mount
    useEffect(() => {
        if (!unitOptions) getUnitOptions()
    }, [])

    // Fetch category, category-fee, and bank account options
    useEffect(() => {
        const condoId = getCondoId()
        api.get('cashflow/categories/', { params: { condo_id: condoId, active: true } })
            .then((res) => setCategories(res.data?.results || res.data || []))
            .catch(() => {})

        api.get('category-fee/', { params: { search: '', page: 1, page_size: 50, company: condoId } })
            .then((res) => setCategoryFees(res.data?.results || res.data || []))
            .catch(() => {})

        api.get('cashflow/managed-bank-accounts/', { params: { condo_id: condoId, is_active: true } })
            .then((res) => {
                const accs = res.data?.results || res.data || []
                setAccounts(accs)
                // Default to FitBank account
                const fitbank = accs.find((a) => a.is_fitbank)
                if (fitbank) setSelectedAccount(fitbank)
            })
            .catch(() => {})
    }, [])

    // Reset payment methods and notifications when account is not FitBank
    useEffect(() => {
        if (!isFitBankAccount) {
            setReqData((prev) => ({
                ...prev,
                payment_methods: [],
                send_email: false,
                send_whatsapp: false,
            }))
        } else {
            setReqData((prev) => ({
                ...prev,
                payment_methods: prev.payment_methods.length > 0 ? prev.payment_methods : ['boleto', 'pix'],
                send_email: true,
                send_whatsapp: true,
            }))
        }
    }, [isFitBankAccount])

    // Generate schedule dates for installments / recurrence
    // Uses due_date as the base day-of-month and skips dates that are already in the past
    const scheduleDates = useMemo(() => {
        const dueDate = moment(reqData.due_date)
        if (!dueDate.isValid() || computedTotal <= 0) return []

        const today = moment().startOf('day')

        const recurrenceMonthsMap = {
            daily: 0,
            weekly: 0,
            monthly: 1,
            quarterly: 3,
            semiannual: 6,
            annual: 12,
        }

        // Helper: advance a date forward until it's not in the past
        const advanceToFuture = (date, addFn) => {
            let d = moment(date)
            // Cap iterations to avoid infinite loops
            for (let safety = 0; safety < 120 && d.isBefore(today); safety++) {
                addFn(d)
            }
            return d
        }

        if (reqData.charge_type === 'installment' && reqData.installments) {
            const n = Math.min(+reqData.installments, 60)
            const perInstallment = Math.round(computedTotal / n)

            // Start from due_date, but if it's in the past, advance month by month
            let firstDate = moment(dueDate)
            if (firstDate.isBefore(today)) {
                // Keep the same day-of-month, advance to the next future month
                firstDate = moment(dueDate)
                while (firstDate.isBefore(today)) {
                    firstDate.add(1, 'months')
                }
            }

            return Array.from({ length: n }, (_, i) => ({
                date: moment(firstDate).add(i, 'months'),
                value: i === n - 1 ? computedTotal - perInstallment * (n - 1) : perInstallment,
                label: `${i + 1}/${n}`,
            }))
        }

        if (reqData.charge_type === 'recurring' && reqData.recurrence) {
            const dates = []
            // Use due_date's day as the billing day, starting from recurrence_start
            const baseDay = dueDate.date()
            const recStart = moment(reqData.recurrence_start || dueDate).startOf('day')
            const startDate = moment(recStart)

            // Set the day-of-month from due_date onto the start
            if (['monthly', 'quarterly', 'semiannual', 'annual'].includes(reqData.recurrence)) {
                startDate.date(Math.min(baseDay, startDate.daysInMonth()))
                // If the billing day falls before the recurrence start, push to next period
                if (startDate.isBefore(recStart)) {
                    startDate.add(recurrenceMonthsMap[reqData.recurrence] || 1, 'months')
                }
            }

            const endDate = reqData.no_end_date ? null : (reqData.recurrence_end ? moment(reqData.recurrence_end) : null)
            const maxItems = endDate ? 120 : 3

            // Determine the increment function
            const getAddFn = (rec) => (d) => {
                if (rec === 'daily') d.add(1, 'days')
                else if (rec === 'weekly') d.add(1, 'weeks')
                else d.add(recurrenceMonthsMap[rec] || 1, 'months')
            }

            const addFn = getAddFn(reqData.recurrence)

            // Advance start date past today if needed
            let current = advanceToFuture(startDate, addFn)

            for (let i = 0; i < maxItems; i++) {
                if (endDate && current.isAfter(endDate)) break
                dates.push({
                    date: moment(current),
                    value: computedTotal,
                    label: reqData.recurrence === 'daily' ? current.format('DD/MM/YYYY') : undefined,
                })
                addFn(current)
            }
            return dates
        }

        return []
    }, [reqData.charge_type, reqData.installments, reqData.recurrence, reqData.due_date, reqData.recurrence_start, reqData.recurrence_end, reqData.no_end_date, computedTotal])

    // Inline contact creation
    const [createContactOpen, setCreateContactOpen] = useState(false)
    const [contactSaving, setContactSaving] = useState(false)
    const [contactErrors, setContactErrors] = useState({})
    const [newContact, setNewContact] = useState({
        name: '',
        cpf: '',
        email: '',
        phone: '',
        number: '',
    })

    const [selectedCategory, setSelectedCategory] = useState(null)
    const [installmentModalOpen, setInstallmentModalOpen] = useState(false)
    const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false)
    const [emailModalOpen, setEmailModalOpen] = useState(false)
    const [whatsappModalOpen, setWhatsappModalOpen] = useState(false)
    const [emailNotifications, setEmailNotifications] = useState({
        notify_on_generate: true,
        send_pending_on_due: true,
        notify_overdue: true,
        resend_overdue_days: 1,
        notify_on_payment: true,
    })
    const [whatsappNotifications, setWhatsappNotifications] = useState({
        notify_on_generate: true,
        send_pending_on_due: false,
        notify_overdue: false,
        resend_overdue_days: 1,
        notify_on_payment: false,
    })

    // --- Card styles ---
    const selectableCardSx = (isSelected) => ({
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: '2px solid',
        borderColor: isSelected ? '#3B82F6' : '#E2E8F0',
        bgcolor: isSelected ? '#EFF6FF' : '#FFFFFF',
        cursor: 'pointer',
        textAlign: 'center',
        transition: 'all 0.15s ease',
        '&:hover': {
            borderColor: '#3B82F6',
            bgcolor: '#F8FAFC',
        },
    })

    const disabledCardSx = {
        flex: 1,
        p: 2,
        borderRadius: 2,
        border: '2px solid #E2E8F0',
        bgcolor: '#F8FAFC',
        textAlign: 'center',
        opacity: 0.55,
        cursor: 'not-allowed',
        position: 'relative',
    }

    const chipSx = (isSelected) => ({
        px: 2.5,
        py: 1,
        borderRadius: 2,
        border: '2px solid',
        borderColor: isSelected ? '#3B82F6' : '#E2E8F0',
        bgcolor: isSelected ? '#EFF6FF' : '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        '&:hover': { borderColor: '#3B82F6', bgcolor: '#F8FAFC' },
    })

    // --- Handlers ---
    const handleChargeTypeSelect = (type) => {
        if (type === 'installment') {
            setReqData((prev) => ({
                ...prev,
                charge_type: 'installment',
                installments: prev.installments || '2',
                recurrence: '',
            }))
            setInstallmentModalOpen(true)
            return
        }
        if (type === 'recurring') {
            setReqData((prev) => ({
                ...prev,
                charge_type: 'recurring',
                installments: '',
                _customInstallments: false,
                recurrence: prev.recurrence || 'monthly',
            }))
            setRecurrenceModalOpen(true)
            return
        }
        // one_time or toggle off
        setReqData((prev) => ({
            ...prev,
            charge_type: prev.charge_type === type ? null : type,
            installments: '',
            _customInstallments: false,
            recurrence: '',
        }))
    }

    const handlePaymentMethodToggle = (method) => {
        setReqData((prev) => {
            const methods = prev.payment_methods.includes(method)
                ? prev.payment_methods.filter((m) => m !== method)
                : [...prev.payment_methods, method]
            return { ...prev, payment_methods: methods }
        })
    }

    const recurrenceOptions = [
        { value: 'daily', label: 'Diária' },
        { value: 'weekly', label: 'Semanal' },
        { value: 'monthly', label: 'Mensal' },
        { value: 'quarterly', label: 'Trimestral' },
        { value: 'semiannual', label: 'Semestral' },
        { value: 'annual', label: 'Anual' },
    ]

    const recurrenceLabel = recurrenceOptions.find((o) => o.value === reqData.recurrence)?.label || ''

    // --- Items ---
    const addItem = () => setItems((prev) => [...prev, { description: '', total: '', category_fee: null }])

    const removeItem = (index) => {
        if (items.length <= 1) return
        setItems((prev) => prev.filter((_, i) => i !== index))
    }

    const updateItem = (index, field, value) => {
        setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
    }

    // --- Contact creation ---
    const handleOpenCreateContact = () => {
        setNewContact({ name: '', cpf: '', email: '', phone: '' })
        setContactErrors({})
        setCreateContactOpen(true)
    }

    const handleCloseCreateContact = () => setCreateContactOpen(false)

    const handleContactFieldChange = (field, value) => {
        setNewContact((prev) => ({ ...prev, [field]: value }))
    }

    const handleSaveContact = async () => {
        const errors = {}
        if (!newContact.name?.trim()) errors.name = 'Nome é obrigatório'
        if (!newContact.cpf?.replace(/\D/g, '')?.trim()) errors.cpf = 'CPF/CNPJ é obrigatório'

        if (Object.keys(errors).length > 0) {
            setContactErrors(errors)
            return
        }

        setContactSaving(true)
        setContactErrors({})

        try {
            const cleanCpf = newContact.cpf.replace(/\D/g, '')
            const payload = {
                condo: +getCondoId(),
                name: newContact.name.trim(),
                cpf: cleanCpf,
                email: newContact.email?.trim() || '',
                phone: newContact.phone?.replace(/\D/g, '') || '',
                number: cleanCpf,
                owner: true,
            }

            const res = await api.post('/unit/', payload)
            const created = res.data

            enqueueSnackbar('Contato criado com sucesso!', { variant: 'success' })
            getUnitOptions()

            setReqData((prev) => ({ ...prev, unit: created.id }))
            setSelectedUnit({
                label: `${created.number} | ${created.name}`,
                value: created.id,
                key: created.id,
            })
            setContactData({
                _unitId: created.id,
                name: created.name || newContact.name.trim(),
                cpf: cleanCpf,
                email: newContact.email?.trim() || '',
                phone: newContact.phone?.replace(/\D/g, '') || '',
            })

            setCreateContactOpen(false)
        } catch (error) {
            const errData = error?.response?.data
            if (errData && typeof errData === 'object') {
                const fieldErrors = {}
                Object.entries(errData).forEach(([key, val]) => {
                    fieldErrors[key] = Array.isArray(val) ? val.join(', ') : val
                })
                setContactErrors(fieldErrors)
            }
            enqueueSnackbar('Erro ao criar contato.', { variant: 'error' })
        } finally {
            setContactSaving(false)
        }
    }

    // --- Save ---
    async function saveReq() {
        if (!reqData?.unit) {
            enqueueSnackbar('Selecione um contato.', { variant: 'warning' })
            return
        }

        const validItems = items.filter((item) => item.description && item.total)
        if (validItems.length === 0) {
            enqueueSnackbar('Adicione pelo menos um item com descrição e valor.', { variant: 'warning' })
            return
        }

        setSaving(true)

        const fees = validItems.map((item) => ({
            description: item.description,
            total: +(item.total?.replace(/\D+/g, '') || 0),
            category: selectedCategory?.id || null,
        }))

        const hasPaymentMethods = reqData.payment_methods.length > 0
        const skipPayment = !hasPaymentMethods

        const data = {
            unit: reqData.unit,
            condo: +getCondoId(),
            total: computedTotal,
            due_date: moment(reqData?.due_date).format('YYYY-MM-DD'),
            description: validItems.map((i) => i.description).join(', '),
            fees,
            send_email_invoice: skipPayment ? false : (reqData.send_email || false),
            send_whatsapp_invoice: skipPayment ? false : (reqData.send_whatsapp || false),
            skip_payment_generation: skipPayment,
            source: 'receivable',
            account: selectedAccount?.id || null,
        }

        if (reqData.charge_type) {
            data.charge_type = reqData.charge_type
            if (reqData.charge_type === 'installment' && reqData.installments) {
                data.installments = +reqData.installments
            }
            if (reqData.charge_type === 'recurring' && reqData.recurrence) {
                data.recurrence = reqData.recurrence
                data.recurrence_start = moment(reqData.recurrence_start).format('YYYY-MM-DD')
                if (!reqData.no_end_date && reqData.recurrence_end) {
                    data.recurrence_end = moment(reqData.recurrence_end).format('YYYY-MM-DD')
                }
            }
        }

        if (hasPaymentMethods) {
            data.payment_methods = reqData.payment_methods
        }

        if (reqData.reference_date) {
            const refMoment = moment(reqData.reference_date)
            data.month = refMoment.month() + 1
            data.year = refMoment.year()
        }

        try {
            await api.post('/charge/', data)
            enqueueSnackbar('Recebimento criado com sucesso!', { variant: 'success' })
            navigate('/financeiro/recebimentos')
        } catch (error) {
            enqueueSnackbar('Erro ao criar recebimento.', { variant: 'error' })
            console.log(error)
        } finally {
            setSaving(false)
        }
    }

    // --- Charge type label ---
    const chargeTypeLabels = { one_time: 'À vista', installment: 'Parcelado', recurring: 'Recorrente' }

    // --- Preview helpers ---
    const validItems = items.filter((item) => item.description && item.total)
    return (
        <>
            <Helmet>
                <title>Novo Recebimento - Bloco B3</title>
            </Helmet>
            <Backdrop sx={{ color: '#fff', zIndex: 1500 }} open={saving}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>
                <Container maxWidth="lg" sx={{ py: 3 }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 1 }}>
                        <IconButton onClick={() => navigate('/financeiro/recebimentos')}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B' }}>
                            Novo Recebimento
                        </Typography>
                    </Box>

                    {/* Two-column layout: Form + Preview */}
                    <Grid container spacing={3}>
                        {/* LEFT: Form */}
                        <Grid item xs={12} md={7}>
                            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <CardContent sx={{ p: 3 }}>

                                    {/* Conta Bancária */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Conta Bancária
                                    </Typography>
                                    <Autocomplete
                                        options={accounts}
                                        getOptionLabel={(opt) => opt.nickname || ''}
                                        value={selectedAccount}
                                        onChange={(_, val) => setSelectedAccount(val)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Selecione uma conta"
                                                size="small"
                                            />
                                        )}
                                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                        sx={{ mb: 2 }}
                                    />

                                    <Divider sx={{ my: 2.5 }} />

                                    {/* 1. Contato */}
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                        <Autocomplete
                                            sx={{ flex: 1 }}
                                            options={unitOptions || []}
                                            getOptionLabel={(opt) => opt.label || ''}
                                            value={selectedUnit}
                                            onChange={(_, val) => {
                                                setSelectedUnit(val)
                                                setReqData((prev) => ({ ...prev, unit: val?.value || null }))
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Contato"
                                                    placeholder="Selecione um contato..."
                                                    size="small"
                                                />
                                            )}
                                            renderOption={(props, option) => {
                                                const typeInfo = CONTACT_TYPE_MAP[option.contact_type]
                                                return (
                                                    <li {...props} key={option.key}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                            <Typography variant="body2">{option.label}</Typography>
                                                            {typeInfo && (
                                                                <Chip
                                                                    label={typeInfo.label}
                                                                    size="small"
                                                                    sx={{
                                                                        ml: 1,
                                                                        height: 20,
                                                                        fontSize: '0.65rem',
                                                                        fontWeight: 600,
                                                                        bgcolor: `${typeInfo.color}14`,
                                                                        color: typeInfo.color,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    </li>
                                                )
                                            }}
                                            isOptionEqualToValue={(opt, val) => opt.value === val?.value}
                                        />
                                        <Button
                                            variant="outlined"
                                            onClick={handleOpenCreateContact}
                                            startIcon={<PersonAdd />}
                                            sx={{
                                                mt: '1px',
                                                minWidth: 'auto',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderColor: '#E2E8F0',
                                                color: '#3B82F6',
                                                whiteSpace: 'nowrap',
                                                height: 40,
                                                '&:hover': { borderColor: '#3B82F6', bgcolor: '#EFF6FF' },
                                            }}
                                        >
                                            Novo Contato
                                        </Button>
                                    </Box>

                                    <Divider sx={{ my: 2.5 }} />

                                    {/* 2. Itens do Recebimento */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Itens do Recebimento
                                    </Typography>

                                    {items.map((item, index) => (
                                        <Grid container spacing={1.5} key={index} sx={{ mb: 1.5, alignItems: 'center' }}>
                                            <Grid item xs={12} sm={6}>
                                                <Autocomplete
                                                    freeSolo
                                                    options={categoryFees}
                                                    getOptionLabel={(opt) => typeof opt === 'string' ? opt : opt.title || ''}
                                                    value={item.description}
                                                    onChange={(_, val) => {
                                                        if (typeof val === 'string') {
                                                            updateItem(index, 'description', val)
                                                            updateItem(index, 'category_fee', null)
                                                        } else if (val) {
                                                            updateItem(index, 'description', val.title)
                                                            updateItem(index, 'category_fee', val.id)
                                                        } else {
                                                            updateItem(index, 'description', '')
                                                            updateItem(index, 'category_fee', null)
                                                        }
                                                    }}
                                                    onInputChange={(_, val, reason) => {
                                                        if (reason === 'input') {
                                                            updateItem(index, 'description', val)
                                                        }
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            size="small"
                                                            label="Descrição"
                                                        />
                                                    )}
                                                    isOptionEqualToValue={(opt, val) => opt.title === val}
                                                />
                                            </Grid>
                                            <Grid item xs={10} sm={5}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label="Valor"
                                                    placeholder="R$ 0,00"
                                                    value={Masks.money(String(item.total ?? ''))}
                                                    onChange={(e) => updateItem(index, 'total', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={2} sm={1}>
                                                <IconButton
                                                    onClick={() => removeItem(index)}
                                                    disabled={items.length <= 1}
                                                    sx={{ color: '#EF4444' }}
                                                >
                                                    <DeleteOutline />
                                                </IconButton>
                                            </Grid>
                                        </Grid>
                                    ))}

                                    <Button
                                        startIcon={<AddCircleOutline />}
                                        onClick={addItem}
                                        sx={{ mt: 1, color: '#3B82F6', textTransform: 'none', fontWeight: 600 }}
                                    >
                                        Adicionar Item
                                    </Button>

                                    <Divider sx={{ my: 2.5 }} />

                                    {/* Categoria */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Categoria
                                    </Typography>
                                    <Autocomplete
                                        options={categories.sort((a, b) => {
                                            const groupA = a.group_detail?.name || 'Sem grupo'
                                            const groupB = b.group_detail?.name || 'Sem grupo'
                                            return groupA.localeCompare(groupB)
                                        })}
                                        getOptionLabel={(opt) => opt.name || ''}
                                        groupBy={(opt) => opt.group_detail?.name || 'Sem grupo'}
                                        value={selectedCategory}
                                        onChange={(_, val) => setSelectedCategory(val)}
                                        renderGroup={(params) => (
                                            <li key={params.key}>
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700,
                                                        color: '#94A3B8',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.05em',
                                                        px: 2,
                                                        pt: 1.5,
                                                        pb: 0.5,
                                                    }}
                                                >
                                                    {params.group}
                                                </Typography>
                                                {params.children}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Selecione uma categoria..."
                                                size="small"
                                            />
                                        )}
                                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                                        sx={{ mb: 2 }}
                                    />

                                    <Divider sx={{ my: 2.5 }} />

                                    {/* 3. Datas */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Datas
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <DesktopDatePicker
                                                label="Data de Vencimento"
                                                {...(reqData.payment_methods.length > 0 && { minDate: moment().add(1, 'days').toDate() })}
                                                value={moment(reqData?.due_date || new Date()).toDate()}
                                                onChange={(e) =>
                                                    setReqData((prev) => ({ ...prev, due_date: moment(e).toDate() }))
                                                }
                                                slotProps={{
                                                    field: { clearable: false, onClear: () => {} },
                                                    textField: { size: 'small', inputProps: { readOnly: true } },
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <DesktopDatePicker
                                                label="Referência (Mês/Ano)"
                                                views={['month', 'year']}
                                                openTo="month"
                                                value={reqData.reference_date}
                                                onChange={(val) =>
                                                    setReqData((prev) => ({ ...prev, reference_date: val }))
                                                }
                                                slotProps={{
                                                    field: { clearable: true, onClear: () => setReqData((prev) => ({ ...prev, reference_date: null })) },
                                                    textField: { size: 'small', inputProps: { readOnly: true } },
                                                }}
                                                sx={{ width: '100%' }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {isFitBankAccount && (<>
                                    <Divider sx={{ my: 2.5 }} />

                                    {/* 4. Meios de pagamento (multi-select) */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Meios de pagamento
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={selectableCardSx(reqData.payment_methods.includes('boleto'))}
                                            onClick={() => handlePaymentMethodToggle('boleto')}
                                        >
                                            <Receipt sx={{ fontSize: 32, color: reqData.payment_methods.includes('boleto') ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.payment_methods.includes('boleto') ? '#3B82F6' : '#1E293B' }}>
                                                Boleto
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                Boleto bancário
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={selectableCardSx(reqData.payment_methods.includes('pix'))}
                                            onClick={() => handlePaymentMethodToggle('pix')}
                                        >
                                            <Pix sx={{ fontSize: 32, color: reqData.payment_methods.includes('pix') ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.payment_methods.includes('pix') ? '#3B82F6' : '#1E293B' }}>
                                                PIX
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                Pagamento instantâneo
                                            </Typography>
                                        </Box>

                                        <Box sx={disabledCardSx}>
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 6,
                                                    bgcolor: '#F59E0B',
                                                    color: '#FFF',
                                                    fontSize: '0.6rem',
                                                    fontWeight: 700,
                                                    px: 0.8,
                                                    py: 0.2,
                                                    borderRadius: 1,
                                                    lineHeight: 1.4,
                                                }}
                                            >
                                                Em breve
                                            </Box>
                                            <CreditCard sx={{ fontSize: 32, color: '#CBD5E1', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#94A3B8' }}>
                                                Cartão de Crédito
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#CBD5E1' }}>
                                                Em integração
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {reqData.payment_methods.length > 0 && (<>
                                        <Divider sx={{ my: 2.5 }} />
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                                Notificações
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                {(() => {
                                                    const emailDisabled = contactData && !contactData.email
                                                    const emailActiveCount = reqData.send_email ? Object.values(emailNotifications).filter((v) => v === true).length : 0
                                                    return (
                                                        <Box
                                                            sx={{
                                                                ...(emailDisabled ? disabledCardSx : selectableCardSx(reqData.send_email)),
                                                            }}
                                                            onClick={emailDisabled ? undefined : () => {
                                                                if (!reqData.send_email) {
                                                                    setReqData((prev) => ({ ...prev, send_email: true }))
                                                                }
                                                                setEmailModalOpen(true)
                                                            }}
                                                        >
                                                            <EmailOutlined sx={{ fontSize: 32, color: emailDisabled ? '#CBD5E1' : (reqData.send_email ? '#3B82F6' : '#94A3B8'), mb: 0.5 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: emailDisabled ? '#94A3B8' : (reqData.send_email ? '#3B82F6' : '#1E293B') }}>
                                                                E-mail
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: emailDisabled ? '#CBD5E1' : '#94A3B8' }}>
                                                                {emailDisabled ? 'Contato sem e-mail' : (reqData.send_email ? `${emailActiveCount} notificações ativas` : 'Configurar notificações')}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                })()}
                                                {(() => {
                                                    const whatsappDisabled = contactData && !contactData.phone
                                                    const whatsappActiveCount = reqData.send_whatsapp ? Object.values(whatsappNotifications).filter((v) => v === true).length : 0
                                                    return (
                                                        <Box
                                                            sx={{
                                                                ...(whatsappDisabled ? disabledCardSx : selectableCardSx(reqData.send_whatsapp)),
                                                            }}
                                                            onClick={whatsappDisabled ? undefined : () => {
                                                                if (!reqData.send_whatsapp) {
                                                                    setReqData((prev) => ({ ...prev, send_whatsapp: true }))
                                                                }
                                                                setWhatsappModalOpen(true)
                                                            }}
                                                        >
                                                            <WhatsApp sx={{ fontSize: 32, color: whatsappDisabled ? '#CBD5E1' : (reqData.send_whatsapp ? '#3B82F6' : '#94A3B8'), mb: 0.5 }} />
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: whatsappDisabled ? '#94A3B8' : (reqData.send_whatsapp ? '#3B82F6' : '#1E293B') }}>
                                                                WhatsApp
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: whatsappDisabled ? '#CBD5E1' : '#94A3B8' }}>
                                                                {whatsappDisabled ? 'Contato sem telefone' : (reqData.send_whatsapp ? `${whatsappActiveCount} notificações ativas` : 'Configurar notificações')}
                                                            </Typography>
                                                        </Box>
                                                    )
                                                })()}
                                            </Box>
                                        </Box>
                                    </>)}
                                    {reqData.payment_methods.length === 0 && (
                                        <Typography variant="caption" sx={{ color: '#F59E0B', display: 'block', mb: 1 }}>
                                            Nenhum meio selecionado. O recebimento será criado sem gerar cobrança.
                                        </Typography>
                                    )}
                                    </>)}

                                    <Divider sx={{ my: 2.5 }} />

                                    {/* 5. Tipo de cobrança (último) */}
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1E293B', mb: 2 }}>
                                        Tipo de cobrança
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                        <Box
                                            sx={selectableCardSx(reqData.charge_type === 'one_time')}
                                            onClick={() => handleChargeTypeSelect('one_time')}
                                        >
                                            <PaymentsOutlined sx={{ fontSize: 32, color: reqData.charge_type === 'one_time' ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.charge_type === 'one_time' ? '#3B82F6' : '#1E293B' }}>
                                                À vista
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                Pagamento único
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={selectableCardSx(reqData.charge_type === 'installment')}
                                            onClick={() => handleChargeTypeSelect('installment')}
                                        >
                                            <ViewAgendaOutlined sx={{ fontSize: 32, color: reqData.charge_type === 'installment' ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.charge_type === 'installment' ? '#3B82F6' : '#1E293B' }}>
                                                Parcelado
                                            </Typography>
                                            {reqData.charge_type === 'installment' && reqData.installments ? (
                                                <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                                                    {reqData.installments}x parcelas
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    Dividido em parcelas
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box
                                            sx={selectableCardSx(reqData.charge_type === 'recurring')}
                                            onClick={() => handleChargeTypeSelect('recurring')}
                                        >
                                            <AutorenewOutlined sx={{ fontSize: 32, color: reqData.charge_type === 'recurring' ? '#3B82F6' : '#94A3B8', mb: 0.5 }} />
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: reqData.charge_type === 'recurring' ? '#3B82F6' : '#1E293B' }}>
                                                Recorrente
                                            </Typography>
                                            {reqData.charge_type === 'recurring' && reqData.recurrence ? (
                                                <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 600 }}>
                                                    {recurrenceLabel}
                                                </Typography>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    Cobrança periódica
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>


                                    <Divider sx={{ my: 2 }} />

                                    {/* Action Buttons */}
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => navigate('/financeiro/recebimentos')}
                                            sx={{
                                                textTransform: 'none',
                                                color: '#64748B',
                                                borderColor: '#E2E8F0',
                                                '&:hover': { borderColor: '#CBD5E1', bgcolor: '#F8FAFC' },
                                            }}
                                        >
                                            Cancelar
                                        </Button>
                                        <Button
                                            variant="contained"
                                            onClick={saveReq}
                                            disabled={saving || computedTotal <= 0}
                                            sx={{
                                                bgcolor: '#3B82F6',
                                                '&:hover': { bgcolor: '#2563EB' },
                                                textTransform: 'none',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Criar Recebimento
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* RIGHT: Resumo */}
                        <Grid item xs={12} md={5}>
                            <Card
                                sx={{
                                    border: '1px solid #E2E8F0',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                    position: 'sticky',
                                    top: 24,
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1E293B', mb: 2 }}>
                                        Resumo
                                    </Typography>

                                    <Box sx={{ pl: 1 }}>
                                        {/* Contato details */}
                                        {contactData && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                                                    <PersonAdd sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                        Cobrar de
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        pl: 4,
                                                        bgcolor: '#F8FAFC',
                                                        borderRadius: 1.5,
                                                        p: 1.5,
                                                        ml: 3.5,
                                                    }}
                                                >
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.3 }}>
                                                        {contactData.name}
                                                    </Typography>
                                                    {contactData.cpf && (
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            {Masks.cpfOrCnpj(contactData.cpf)}
                                                        </Typography>
                                                    )}
                                                    {contactData.email && (
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            {contactData.email}
                                                        </Typography>
                                                    )}
                                                    {contactData.phone && (
                                                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                            {Masks.phone(contactData.phone)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Vencimento */}
                                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                            <CalendarToday sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                    {moment(reqData.due_date).format('DD [de] MMMM [de] YYYY')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                                                    Vencimento
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Items */}
                                        {validItems.length > 0 && (
                                            <Box sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 1.5, mb: 1 }}>
                                                    <Receipt sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                        Itens
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ pl: 4 }}>
                                                    {validItems.map((item, i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                py: 0.5,
                                                                borderBottom: i < validItems.length - 1 ? '1px solid #F1F5F9' : 'none',
                                                            }}
                                                        >
                                                            <Typography variant="caption" sx={{ color: '#475569' }}>
                                                                {item.description}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 600 }}>
                                                                {Masks.money(String(item.total))}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Categoria */}
                                        {selectedCategory && (
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                                <ViewAgendaOutlined sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.3 }}>
                                                        Categoria
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#475569' }}>
                                                        {selectedCategory.name}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Conta Bancária */}
                                        {selectedAccount && (
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                                <PaymentsOutlined sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.3 }}>
                                                        Conta Bancária
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#475569' }}>
                                                        {selectedAccount.nickname}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Total */}
                                        {computedTotal !== 0 && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    bgcolor: computedTotal < 0 ? '#FEF2F2' : '#F1F5F9',
                                                    borderRadius: 1.5,
                                                    px: 2,
                                                    py: 1.5,
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: computedTotal < 0 ? '#DC2626' : '#475569' }}>
                                                    Total
                                                </Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: computedTotal < 0 ? '#DC2626' : '#1E293B' }}>
                                                    {computedTotal < 0 ? '-' : ''}{Masks.money(String(Math.abs(computedTotal)))}
                                                </Typography>
                                            </Box>
                                        )}

                                        <Divider sx={{ my: 1.5 }} />

                                        {/* Payment methods */}
                                        {isFitBankAccount && reqData.payment_methods.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, mt: 2 }}>
                                                <Pix sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                                        Meios de pagamento
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {reqData.payment_methods.map((m) => (
                                                            <Chip
                                                                key={m}
                                                                label={m === 'boleto' ? 'Boleto' : 'PIX'}
                                                                size="small"
                                                                icon={m === 'boleto' ? <Receipt sx={{ fontSize: 14 }} /> : <Pix sx={{ fontSize: 14 }} />}
                                                                sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#3B82F6', fontSize: '0.75rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Notifications */}
                                        {isFitBankAccount && (reqData.send_email || reqData.send_whatsapp) && (
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                                <EmailOutlined sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B', mb: 0.5 }}>
                                                        Notificações
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {reqData.send_email && (
                                                            <Chip
                                                                label="E-mail"
                                                                size="small"
                                                                icon={<EmailOutlined sx={{ fontSize: 14 }} />}
                                                                sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#3B82F6', fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                        {reqData.send_whatsapp && (
                                                            <Chip
                                                                label="WhatsApp"
                                                                size="small"
                                                                icon={<WhatsApp sx={{ fontSize: 14 }} />}
                                                                sx={{ fontWeight: 600, bgcolor: '#EFF6FF', color: '#3B82F6', fontSize: '0.75rem' }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Charge type + Schedule */}
                                        {reqData.charge_type && (
                                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                                <EventRepeat sx={{ fontSize: 18, color: '#3B82F6', mt: 0.3 }} />
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E293B' }}>
                                                        {chargeTypeLabels[reqData.charge_type]}
                                                    </Typography>

                                                    {reqData.charge_type === 'one_time' && (
                                                        <Typography variant="caption" sx={{ color: '#64748B' }}>
                                                            Valor devido: {Masks.money(String(computedTotal))}
                                                        </Typography>
                                                    )}

                                                    {reqData.charge_type === 'installment' && reqData.installments && (
                                                        <>
                                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                                {reqData.installments}x de {Masks.money(String(Math.round(computedTotal / +reqData.installments)))}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
                                                                {reqData.generate_days_before > 0
                                                                    ? `Fatura gerada ${reqData.generate_days_before} ${reqData.generate_days_before === 1 ? 'dia' : 'dias'} antes do vencimento`
                                                                    : 'Fatura gerada manualmente'}
                                                            </Typography>
                                                        </>
                                                    )}

                                                    {reqData.charge_type === 'recurring' && reqData.recurrence && (
                                                        <>
                                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                                                                Frequência: {recurrenceLabel}
                                                                {' \u2022 '}
                                                                {reqData.no_end_date ? 'Sem data de fim' : `Até ${moment(reqData.recurrence_end).format('DD/MM/YYYY')}`}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 1 }}>
                                                                {reqData.generate_days_before > 0
                                                                    ? `Fatura gerada ${reqData.generate_days_before} ${reqData.generate_days_before === 1 ? 'dia' : 'dias'} antes do vencimento`
                                                                    : 'Fatura gerada manualmente'}
                                                            </Typography>
                                                        </>
                                                    )}

                                                    {/* Schedule table */}
                                                    {scheduleDates.length > 0 && (
                                                        <Box
                                                            sx={{
                                                                mt: 1,
                                                                border: '1px solid #E2E8F0',
                                                                borderRadius: 1.5,
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    bgcolor: '#F8FAFC',
                                                                    px: 1.5,
                                                                    py: 0.75,
                                                                    borderBottom: '1px solid #E2E8F0',
                                                                }}
                                                            >
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.7rem' }}>
                                                                    {reqData.charge_type === 'installment' ? 'PARCELA' : 'COBRANÇA'}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748B', fontSize: '0.7rem' }}>
                                                                    VALOR
                                                                </Typography>
                                                            </Box>
                                                            {scheduleDates.map((entry, i) => (
                                                                <Box
                                                                    key={i}
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        px: 1.5,
                                                                        py: 0.75,
                                                                        borderBottom: i < scheduleDates.length - 1 ? '1px solid #F1F5F9' : 'none',
                                                                        bgcolor: i % 2 === 0 ? '#FFFFFF' : '#FAFBFC',
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        {entry.label && (
                                                                            <Typography variant="caption" sx={{ color: '#3B82F6', fontWeight: 600, mr: 1, fontSize: '0.7rem' }}>
                                                                                {entry.label}
                                                                            </Typography>
                                                                        )}
                                                                        <Typography variant="caption" sx={{ color: '#475569', fontSize: '0.75rem' }}>
                                                                            {entry.date.format('DD/MM/YYYY')}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="caption" sx={{ color: '#1E293B', fontWeight: 600, fontSize: '0.75rem' }}>
                                                                        {Masks.money(String(entry.value))}
                                                                    </Typography>
                                                                </Box>
                                                            ))}
                                                            {reqData.charge_type === 'recurring' && reqData.no_end_date && (
                                                                <Box sx={{ textAlign: 'center', py: 0.75, bgcolor: '#F8FAFC', borderTop: '1px solid #E2E8F0' }}>
                                                                    <Typography variant="caption" sx={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '0.7rem' }}>
                                                                        ... continua sem data de fim
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Empty state */}
                                        {!contactData && validItems.length === 0 && (
                                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                                                    Preencha o formulário para visualizar o resumo
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Create Contact Dialog */}
            <Dialog
                open={createContactOpen}
                onClose={handleCloseCreateContact}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1 }}>
                    Novo Contato
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Preencha os dados do contato. Após salvar, ele será selecionado automaticamente.
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Nome *"
                                value={newContact.name}
                                onChange={(e) => handleContactFieldChange('name', e.target.value)}
                                error={!!contactErrors.name}
                                helperText={contactErrors.name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="CPF/CNPJ *"
                                value={Masks.cpfOrCnpj(newContact.cpf)}
                                onChange={(e) => handleContactFieldChange('cpf', e.target.value)}
                                error={!!contactErrors.cpf}
                                helperText={contactErrors.cpf}
                                inputProps={{ maxLength: 18 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="E-mail"
                                type="email"
                                value={newContact.email}
                                onChange={(e) => handleContactFieldChange('email', e.target.value)}
                                error={!!contactErrors.email}
                                helperText={contactErrors.email}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Telefone"
                                value={Masks.phone(newContact.phone || '')}
                                onChange={(e) => handleContactFieldChange('phone', e.target.value)}
                                error={!!contactErrors.phone}
                                helperText={contactErrors.phone}
                                inputProps={{ maxLength: 15 }}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCloseCreateContact}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveContact}
                        disabled={contactSaving}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {contactSaving ? <CircularProgress size={20} color="inherit" /> : 'Salvar Contato'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Installment Modal */}
            <Dialog
                open={installmentModalOpen}
                onClose={() => setInstallmentModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1 }}>
                    Número de Parcelas
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Selecione a quantidade de parcelas para o recebimento.
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                        {Array.from({ length: 23 }, (_, i) => i + 2).map((n) => (
                            <Grid item xs={2} key={n}>
                                <Box
                                    onClick={() => setReqData((prev) => ({
                                        ...prev,
                                        installments: String(n),
                                        _customInstallments: false,
                                    }))}
                                    sx={{
                                        ...chipSx(+reqData.installments === n && !reqData._customInstallments),
                                        textAlign: 'center',
                                        py: 1,
                                        px: 0.5,
                                    }}
                                >
                                    <Typography variant="body2" sx={{
                                        fontWeight: 600,
                                        fontSize: '0.85rem',
                                        color: +reqData.installments === n && !reqData._customInstallments ? '#3B82F6' : '#1E293B',
                                    }}>
                                        {n}x
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                        <Grid item xs={2}>
                            <Box
                                onClick={() => setReqData((prev) => ({
                                    ...prev,
                                    installments: '',
                                    _customInstallments: true,
                                }))}
                                sx={{
                                    ...chipSx(!!reqData._customInstallments),
                                    textAlign: 'center',
                                    py: 1,
                                    px: 0.5,
                                }}
                            >
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    color: reqData._customInstallments ? '#3B82F6' : '#1E293B',
                                }}>
                                    Outro
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    {reqData._customInstallments && (
                        <TextField
                            size="small"
                            label="Parcelas personalizadas"
                            type="number"
                            value={reqData.installments}
                            onChange={(e) => setReqData((prev) => ({ ...prev, installments: e.target.value }))}
                            inputProps={{ min: 2 }}
                            sx={{ mt: 2, width: 220 }}
                            autoFocus
                        />
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block' }}>
                        Geração automática da fatura
                    </Typography>
                    <TextField
                        select
                        size="small"
                        label="Gerar fatura antes do vencimento"
                        value={reqData.generate_days_before}
                        onChange={(e) => setReqData((prev) => ({ ...prev, generate_days_before: +e.target.value }))}
                        sx={{ width: 300 }}
                    >
                        <MenuItem value={0}>Gerar manualmente</MenuItem>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
                            <MenuItem key={d} value={d}>
                                {d} {d === 1 ? 'dia' : 'dias'} antes do vencimento
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setReqData((prev) => ({ ...prev, charge_type: null, installments: '', _customInstallments: false }))
                            setInstallmentModalOpen(false)
                        }}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setInstallmentModalOpen(false)}
                        disabled={!reqData.installments}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Recurrence Modal */}
            <Dialog
                open={recurrenceModalOpen}
                onClose={() => setRecurrenceModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1 }}>
                    Configurar Recorrência
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Defina a frequência e a duração da cobrança recorrente.
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block' }}>
                        Frequência
                    </Typography>
                    <Grid container spacing={1} sx={{ mb: 3 }}>
                        {recurrenceOptions.map((opt) => (
                            <Grid item xs={4} key={opt.value}>
                                <Box
                                    onClick={() => setReqData((prev) => ({ ...prev, recurrence: prev.recurrence === opt.value ? '' : opt.value }))}
                                    sx={{
                                        ...chipSx(reqData.recurrence === opt.value),
                                        textAlign: 'center',
                                        py: 1,
                                        px: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem', color: reqData.recurrence === opt.value ? '#3B82F6' : '#1E293B' }}>
                                        {opt.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block' }}>
                        Duração
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <DesktopDatePicker
                                label="Data de início"
                                value={reqData.recurrence_start}
                                onChange={(val) =>
                                    setReqData((prev) => ({ ...prev, recurrence_start: val }))
                                }
                                slotProps={{
                                    textField: { size: 'small', inputProps: { readOnly: true } },
                                }}
                                sx={{ width: '100%' }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {reqData.no_end_date ? (
                                <Box
                                    sx={{
                                        height: 40,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px dashed #CBD5E1',
                                        borderRadius: 1,
                                        cursor: 'pointer',
                                        '&:hover': { borderColor: '#3B82F6', bgcolor: '#F8FAFC' },
                                    }}
                                    onClick={() => setReqData((prev) => ({ ...prev, no_end_date: false, recurrence_end: moment().add(1, 'year').toDate() }))}
                                >
                                    <Typography variant="body2" sx={{ color: '#64748B', fontWeight: 500 }}>
                                        Sem data de fim (vitalício)
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <DesktopDatePicker
                                        label="Data de fim"
                                        value={reqData.recurrence_end}
                                        minDate={reqData.recurrence_start || new Date()}
                                        onChange={(val) =>
                                            setReqData((prev) => ({ ...prev, recurrence_end: val }))
                                        }
                                        slotProps={{
                                            textField: { size: 'small', inputProps: { readOnly: true } },
                                        }}
                                        sx={{ flex: 1 }}
                                    />
                                    <Button
                                        size="small"
                                        onClick={() => setReqData((prev) => ({ ...prev, no_end_date: true, recurrence_end: null }))}
                                        sx={{ textTransform: 'none', color: '#64748B', minWidth: 'auto', fontSize: '0.75rem' }}
                                    >
                                        Vitalício
                                    </Button>
                                </Box>
                            )}
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block' }}>
                        Geração automática da fatura
                    </Typography>
                    <TextField
                        select
                        size="small"
                        label="Gerar fatura antes do vencimento"
                        value={reqData.generate_days_before}
                        onChange={(e) => setReqData((prev) => ({ ...prev, generate_days_before: +e.target.value }))}
                        sx={{ width: 300 }}
                    >
                        <MenuItem value={0}>Gerar manualmente</MenuItem>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
                            <MenuItem key={d} value={d}>
                                {d} {d === 1 ? 'dia' : 'dias'} antes do vencimento
                            </MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setReqData((prev) => ({ ...prev, charge_type: null, recurrence: '' }))
                            setRecurrenceModalOpen(false)
                        }}
                        sx={{ textTransform: 'none', color: '#64748B' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setRecurrenceModalOpen(false)}
                        disabled={!reqData.recurrence}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Email Notifications Modal */}
            <Dialog
                open={emailModalOpen}
                onClose={() => setEmailModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailOutlined sx={{ color: '#3B82F6' }} />
                    Notificações por E-mail
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Configure quais notificações serão enviadas por e-mail ao cliente.
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Ao gerar fatura
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={emailNotifications.notify_on_generate} onChange={(e) => setEmailNotifications((prev) => ({ ...prev, notify_on_generate: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Notificar cliente ao gerar fatura</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        No dia do vencimento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={emailNotifications.send_pending_on_due} onChange={(e) => setEmailNotifications((prev) => ({ ...prev, send_pending_on_due: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Enviar cobranças pendentes no dia do vencimento</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Após o vencimento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={emailNotifications.notify_overdue} onChange={(e) => setEmailNotifications((prev) => ({ ...prev, notify_overdue: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Avisar sobre atrasos e falhas nos pagamentos</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />
                    {emailNotifications.notify_overdue && (
                        <Box sx={{ ml: 4, mb: 1 }}>
                            <TextField
                                select
                                size="small"
                                label="Reenviar cobranças a cada"
                                value={emailNotifications.resend_overdue_days}
                                onChange={(e) => setEmailNotifications((prev) => ({ ...prev, resend_overdue_days: +e.target.value }))}
                                sx={{ width: 250 }}
                            >
                                {Array.from({ length: 10 }, (_, i) => i + 1).map((d) => (
                                    <MenuItem key={d} value={d}>
                                        {d} {d === 1 ? 'dia' : 'dias'}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Após o pagamento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={emailNotifications.notify_on_payment} onChange={(e) => setEmailNotifications((prev) => ({ ...prev, notify_on_payment: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Avisar quando os pagamentos forem confirmados</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setReqData((prev) => ({ ...prev, send_email: false }))
                            setEmailModalOpen(false)
                        }}
                        sx={{ textTransform: 'none', color: '#EF4444' }}
                    >
                        Desativar E-mail
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setEmailModalOpen(false)}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* WhatsApp Notifications Modal */}
            <Dialog
                open={whatsappModalOpen}
                onClose={() => setWhatsappModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, color: '#1E293B', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WhatsApp sx={{ color: '#3B82F6' }} />
                    Notificações por WhatsApp
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                        Configure quais notificações serão enviadas por WhatsApp ao cliente. Envios adicionais são cobrados por mensagem.
                    </Typography>

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Ao gerar fatura
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={whatsappNotifications.notify_on_generate} onChange={(e) => setWhatsappNotifications((prev) => ({ ...prev, notify_on_generate: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Notificar cliente ao gerar fatura</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        No dia do vencimento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={whatsappNotifications.send_pending_on_due} onChange={(e) => setWhatsappNotifications((prev) => ({ ...prev, send_pending_on_due: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Enviar cobranças pendentes no dia do vencimento</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Após o vencimento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={whatsappNotifications.notify_overdue} onChange={(e) => setWhatsappNotifications((prev) => ({ ...prev, notify_overdue: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Enviar aviso único de atraso após o vencimento</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />

                    <Divider sx={{ my: 1.5 }} />

                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, mb: 1, display: 'block', textTransform: 'uppercase' }}>
                        Após o pagamento
                    </Typography>
                    <FormControlLabel
                        control={<Checkbox checked={whatsappNotifications.notify_on_payment} onChange={(e) => setWhatsappNotifications((prev) => ({ ...prev, notify_on_payment: e.target.checked }))} sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }} />}
                        label={<Typography variant="body2">Avisar quando os pagamentos forem confirmados</Typography>}
                        sx={{ mb: 0.5, ml: 0 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => {
                            setReqData((prev) => ({ ...prev, send_whatsapp: false }))
                            setWhatsappModalOpen(false)
                        }}
                        sx={{ textTransform: 'none', color: '#EF4444' }}
                    >
                        Desativar WhatsApp
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => setWhatsappModalOpen(false)}
                        sx={{
                            bgcolor: '#3B82F6',
                            '&:hover': { bgcolor: '#2563EB' },
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

        </>
    )
}

export default ReceivablesCreate
